<script>
  import { computed } from '@nuxtjs/composition-api'
  import { productGetters } from '@/composables/propeller/getters'

  import { useAttribute, useListAttribute } from '@/composables/useAttribute'
  import Badge from '@/components/Ecommerce/Badge'
  import CardBase from '@/components/CardBase'
  import ProductPrice from '@/components/Ecommerce/Products/ProductPrice'
  import ProductAddToCart from '@/components/Ecommerce/Products/ProductAddToCart'
  import { productAttributes } from '@/constants/products.module'
  import { setProductsListTracking } from '~/utils/utils'
  import { gtmSendEvent } from '~/utils/gtm-send-event'

  export default {
    components: {
      Badge,
      CardBase,
      ProductPrice,
      ProductAddToCart
    },
    props: {
      product: {
        type: Object,
        default: null
      },
      minimal: {
        type: Boolean,
        default: false
      },
      disabled: {
        type: Boolean,
        default: false
      },
      active: {
        type: Boolean,
        default: false
      },
      interactive: {
        type: Boolean,
        default: true
      }
    },
    setup (props) {
      const trackProductClick = (product, productType, level) => {
        const productList = setProductsListTracking([{
          ...product,
          name: productGetters.getName(product),
          productType,
          level,
        }])
        gtmSendEvent('select_item', {
          ecommerce: {
            currency: 'EUR',
            items: productList
          },
          item_list_name: 'Productpagina',
          item_list_id: 'productpagina'
        }, null, true)
      }

      return {
        // Fall back to the full name: the grid listing query may omit the
        // localized `shortNames`, in which case `getShortName` returns '' and the
        // card title would render blank.
        name: computed(() => productGetters.getShortName(props.product) || productGetters.getName(props.product)),
        id: computed(() => productGetters.getId(props.product) || props.product.id),
        coverImage: computed(() => productGetters.getCoverImage(props.product)),
        price: computed(() => productGetters.getPrice(props.product)),
        slug: computed(() => productGetters.getSlug(props.product) || props.product.slug),
        productType: useAttribute(props.product, productAttributes.brand),
        levels: computed(() => useListAttribute(props.product, productAttributes.educationType).value.join(', ')),
        attributes: computed(() => productGetters.getAttributes(props.product)),
        isOrderable: computed(() => props.product.orderable !== 'N'),
        trackProductClick
      }
    }
  }
</script>

<template>
  <transition
    name="fade"
    mode="out-in"
  >
    <div
      class="h-full"
      @click="() => trackProductClick(product, productType, levels, price)"
    >
      <component
        :is="interactive ? 'nuxt-link' : 'div'"
        :to="interactive ? `/producten/${id}/${slug}` : ''"
        class="h-full"
      >
        <card-base
          class="w-full h-full card-product"
          :interactive="!minimal"
          :class="{
            'is-minimal': minimal,
            'is-disabled': disabled,
            'is-active': active
          }"
        >
          <figure class="flex-shrink-0 mb-4 pointer-events-none product-image">
            <img
              :src="coverImage"
              :alt="name"
              class="mx-auto"
            >
          </figure>
          <Badge
            v-if="productType !== 'Voordeelpakket' && productType !== 'Examenbuddy'"
            small
            secondary
            rounded
          >
            Tot 15% stapelkorting
          </Badge>
          <span
            v-else
            style="height: 26px;"
          ></span>
          <header>
            <h3
              class="product-title"
              :title="name"
            >
              {{ productType }} <br>
              {{ name }}
            </h3>

            <p class="text-xs text-gray-500 product-category h-14">
              {{ levels }}
            </p>
          </header>

          <footer v-if="!minimal">
            <div class="flex items-center justify-between pt-4">
              <div class="product-price">
                <ProductPrice
                  :price="price.regular"
                  :discount="price.special"
                  direction="col"
                />
              </div>
              <div>
                <ProductAddToCart
                  class="w-12 h-12 bg-orange-300 product-add-to-cart"
                  :disabled="!isOrderable"
                  :product="product"
                  small
                />
              </div>
            </div>
          </footer>
        </card-base>
      </component>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
.card-product {
  @apply h-full p-4 flex flex-col justify-between;

  .product-title {
    @apply text-lg truncate mt-3;
    line-height: 1.65rem;
  }

  .product-category,
  .product-subtitle {
    @apply text-gray-600;
  }

  &.is-minimal {
    @apply p-3;

    header {
      width: 73%;
      @screen md {
        @apply w-full;
      }
    }

    .product-title {
      @apply text-base;
      @apply overflow-hidden;
      @apply truncate;
      @apply whitespace-nowrap;
      @apply overflow-ellipsis;
    }

    .product-category,
    .product-subtitle {
      @apply text-xs;
    }

    figure {
      @apply mb-0;
    }

    @apply flex flex-row-reverse justify-end items-center;
    .product-image {
      @apply mt-0;
      @apply w-20;
    }

    .product-title,
    .product-category,
    .product-subtitle {
      @apply text-left;
    }

    @screen sm {
      @apply p-6;
      @apply block;
      .product-image {
        @apply mt-6;
        @apply w-auto;
      }
      .product-title,
      .product-category,
      .product-subtitle {
        @apply text-center;
      }
    }
  }

  &.is-active {
    @apply shadow-big;
  }

  &.is-disabled {
    @apply shadow-none;
    @apply border border-gray-100;
  }

  .product-price {
    @apply text-blue-500;
  }
  .product-add-to-cart  {
    padding: 6px !important;
    width: auto !important;
    height: auto !important;
    @apply bg-orange-300;
  }

  .product-image img {
    max-height: 128px;
  }
}
</style>
